import {
  Axe,
  AxeActor,
  AxeEvent,
  AxeStatus,
  AxeUpdate,
  CollateralBasketType,
  Currency,
  Direction,
  EntityType,
  Instrument,
  Organisation,
  PlatformFeature,
  Product,
  ProductSector,
  ReferenceRate,
  RepoType,
  Side,
  SubscriptionIdentifier,
  TradingEntity,
  ValidationIssue
} from 'api'
import { applyUpdatesForWindow, compareRepositoryEntryUpdateCreatedAtDesc } from 'model'
import { createSelector } from '@reduxjs/toolkit'
import { GlobalState } from 'store'
import { RepositoryEntry } from 'store/repository'
import { DateLike, getCurrentWindowId, Nullable, ParametricSelector } from 'utils'
import { isNotEmpty, isNotNil } from 'utils/predicate'
import { createAuthorisedUserSelector } from './connectionSelectors'
import { createEntityGetterSelector } from './entitySelectors'
import { axeStoreSelector } from './storeSelectors'
import { AxeFieldName, AxeFieldNames } from 'view/routes/axeBoard/components/AxeGridFields'
import { Locale, Translation } from 'locale'
import { createOrganisationsTradingEntityRelationshipsGetterSelector } from './tradingEntitySelectors/tradingEntitySelectors'
import { currentOrganisationIdSelector } from './adminSelectors'

export const createAxeUpdateEntriesSelector = (subscriptionIdentifiers?: SubscriptionIdentifier[]) =>
  createSelector([axeStoreSelector], axeStore => {
    return Object.values(axeStore)
      .filter(e => (subscriptionIdentifiers ?? []).every(identifier => e.lastUpdate.subscriptionIdentifiers.includes(identifier)))
      .sort(compareRepositoryEntryUpdateCreatedAtDesc)
  })

export interface AxeDetail {
  availableEvents: AxeEvent[]
  availableRepoTypes: RepoType[]
  status: AxeStatus
  axe: Axe
  direction: Nullable<Direction>
  instrument: Nullable<Instrument>
  createdAt: DateLike
  updatedAt: DateLike
  createdBy: string
  updatedBy: string
  isinBui: Nullable<string>
  initiatorTradingEntity: Nullable<TradingEntity>
  ownTradingEntity: Nullable<TradingEntity>
  responderOrganisations: Organisation[]
  counterpartyOrganisationLabel: string
  initiatorOrganisation: Nullable<Organisation>
  ownOrganisation: Nullable<Organisation>
  validationIssues: ValidationIssue[]
  cusip: Nullable<string>
  productSector: Nullable<ProductSector>
  product: Nullable<Product>
  referenceRate: Nullable<ReferenceRate>
  coupon: Nullable<number>
  issuer: Nullable<string>
  permittedCurrencies: Currency[]
  instrumentValidationFieldName: AxeFieldName
  myEntityShortName: Nullable<string>
  isResponderView: boolean
  side: Side
}

const createAxeDetailsSelector = <P = unknown>(
  axeUpdateEntriesSelector: ParametricSelector<GlobalState, P, RepositoryEntry<AxeUpdate>[]>,
  viewAs: AxeActor
): ParametricSelector<GlobalState, unknown, AxeDetail[]> =>
  createSelector(
    [
      axeUpdateEntriesSelector,
      createEntityGetterSelector(EntityType.COLLATERAL),
      createEntityGetterSelector(EntityType.COLLATERAL_BASKET),
      createEntityGetterSelector(EntityType.TRADING_ENTITY),
      createEntityGetterSelector(EntityType.ORGANISATION),
      createEntityGetterSelector(EntityType.PRODUCT),
      createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
      createEntityGetterSelector(EntityType.REFERENCE_RATE),
      createOrganisationsTradingEntityRelationshipsGetterSelector(),
      createAuthorisedUserSelector()
    ],
    (
      axeEntries,
      getCollateral,
      getCollateralBasket,
      getTradingEntity,
      getOrganisation,
      getProduct,
      getProductSector,
      getReferenceRate,
      getOrganisationsTradingEntityRelationships,
      authorisedUser
    ) => {
      return axeEntries.map(axeEntry => {
        const axe = applyUpdatesForWindow(axeEntry, getCurrentWindowId())
        const isResponderView = viewAs === AxeActor.RESPONDER

        const direction =
          isNotNil(axe.direction) && isResponderView ? (axe.direction === Direction.BUY ? Direction.SELL : Direction.BUY) : axe.direction

        const { lastUpdate } = axeEntry

        const validationIssues = lastUpdate.validationIssues
        const availableEvents = lastUpdate.availableEvents
        const status = lastUpdate.axeStatus
        const createdAt = lastUpdate.createdAt
        const createdBy = lastUpdate.createdBy
        const updatedAt = lastUpdate.updatedAt
        const updatedBy = lastUpdate.updatedBy

        const collateral = axe.collateralId ? getCollateral(axe.collateralId) : null
        const collateralBasket = axe.collateralBasketId ? getCollateralBasket(axe.collateralBasketId) : null

        const coupon = isNotNil(collateral) ? collateral.coupon : null
        const issuer = isNotNil(collateral) ? collateral.issuer : null

        const productSectorId = isNotNil(collateral)
          ? collateral.productSectorId
          : isNotNil(collateralBasket)
          ? collateralBasket.productSectorId
          : null

        const productSector = isNotNil(productSectorId) ? getProductSector(productSectorId) : null
        const product = isNotNil(productSector) ? getProduct(productSector.productId) : null
        const referenceRate = isNotNil(axe.referenceRate) ? getReferenceRate(axe.referenceRate) : null

        const cusip = isNotNil(collateral) ? collateral.cusip : collateralBasket?.collateralBasketCusip ?? null

        const isinBui = isNotNil(collateral)
          ? collateral.isin
          : productSector?.basketType === CollateralBasketType.DBV
          ? collateralBasket?.collateralBasketIsin ?? null
          : collateralBasket?.id ?? null

        const initiatorTradingEntity = axe.initiatorTradingEntityId ? getTradingEntity(axe.initiatorTradingEntityId) : null
        const initiatorOrganisation = axe.initiatorOrganisationId ? getOrganisation(axe.initiatorOrganisationId) : null
        const responderOrganisations = isNotNil(axe.responderOrganisationIds)
          ? axe.responderOrganisationIds.map(id => getOrganisation(id)).filter(isNotNil)
          : []

        const availableRepoTypes = isNotNil(productSector)
          ? initiatorOrganisation?.enabledFeatures.includes(PlatformFeature.ENABLE_STOCK_BORROWING_LENDING) &&
            responderOrganisations.every(responderOrganisation =>
              responderOrganisation.enabledFeatures.includes(PlatformFeature.ENABLE_STOCK_BORROWING_LENDING)
            )
            ? productSector.supportedRepoTypes
            : productSector.supportedRepoTypes.filter(r => r !== RepoType.STOCK_BORROWING_LENDING)
          : []

        const ownOrganisation = isNotNil(authorisedUser.organisationId) ? getOrganisation(axe.initiatorOrganisationId) : null

        const authorisedUserTradingEntities = isNotNil(authorisedUser.tradingEntityDetails)
          ? authorisedUser.tradingEntityDetails.map(d => getTradingEntity(d.tradingEntityId)).filter(isNotNil)
          : []

        const ownTradingEntity = isResponderView
          ? authorisedUserTradingEntities.length === 1
            ? authorisedUserTradingEntities[0]
            : null
          : initiatorTradingEntity

        const instrument = collateral ?? collateralBasket

        const possibleResponderOrganisations =
          instrument && initiatorTradingEntity && !isResponderView
            ? getOrganisationsTradingEntityRelationships(initiatorTradingEntity.id, Side.SELL, instrument)
            : []

        const counterpartyOrganisationLabel = isResponderView
          ? initiatorOrganisation?.legalName ?? ''
          : responderOrganisations.length === possibleResponderOrganisations.length
          ? Locale.translate(Translation.TC_ALL)
          : responderOrganisations.length > 1
          ? Locale.translate(Translation.SC_MULTIPLE)
          : responderOrganisations.length === 1
          ? responderOrganisations[0].legalName
          : ''

        const myEntityShortName = isResponderView
          ? ownTradingEntity?.shortName
            ? ownTradingEntity.shortName
            : null
          : initiatorTradingEntity?.shortName
          ? initiatorTradingEntity.shortName
          : null

        const axeDetail: AxeDetail = {
          availableEvents,
          availableRepoTypes,
          axe,
          responderOrganisations,
          counterpartyOrganisationLabel,
          coupon,
          createdAt,
          createdBy,
          updatedAt,
          updatedBy,
          cusip,
          direction,
          initiatorTradingEntity,
          initiatorOrganisation,
          ownOrganisation,
          ownTradingEntity,
          instrument,
          instrumentValidationFieldName: isNotNil(collateral) ? AxeFieldNames.COLLATERAL : AxeFieldNames.COLLATERAL_BASKET,
          isinBui,
          issuer,
          permittedCurrencies: [Currency.GBP, Currency.EUR],
          product,
          productSector,
          referenceRate,
          status,
          validationIssues,
          myEntityShortName,
          isResponderView,
          side: isResponderView ? Side.BUY : Side.SELL
        }

        return axeDetail
      })
    }
  )

export const createInitiatorAxeDetailsSelector = () =>
  createAxeDetailsSelector(
    createSelector(
      [createAxeUpdateEntriesSelector([SubscriptionIdentifier.AXE_TODAY_INITIATOR]), currentOrganisationIdSelector],
      (entries, currentOrganisationId) =>
        isNotNil(currentOrganisationId) && isNotEmpty(currentOrganisationId)
          ? entries.filter(e => e.lastUpdate.entity.initiatorOrganisationId === currentOrganisationId)
          : entries
    ),
    AxeActor.INITIATOR
  )

export const createResponderAxeDetailsSelector = () =>
  createAxeDetailsSelector(createAxeUpdateEntriesSelector([SubscriptionIdentifier.AXE_TODAY_RESPONDER]), AxeActor.RESPONDER)

const isNonCancelledAxeDetail = (axeDetail: AxeDetail): boolean => {
  return axeDetail.status !== AxeStatus.CANCELLED
}

export const initiatorAxeDetailsCountSelector = createSelector(
  createInitiatorAxeDetailsSelector(),
  axeDetails => axeDetails.filter(isNonCancelledAxeDetail).length
)

export const responderAxeDetailsCountSelector = createSelector(
  createResponderAxeDetailsSelector(),
  axeDetails => axeDetails.filter(isNonCancelledAxeDetail).length
)
